
/* eslint-disable vue/no-mutating-props */
// `target` is the checkup page's shared form-model object (mixins/checkup-page.js
// pattern, matching components/schedule/v2/steps/*), passed by reference so this
// step can push/splice its pharmacies list and flip no_pharmacies /
// prefer_mail_order / other_pharmacies_ok in place by design.
//
// Icons: the demo (temp-data/pre-consult-survey-redesign/Survey.Redesign.Demo_7_31_6.html
// L1382-1426) uses a truck icon for "Mail-order pharmacy" and a refresh icon
// for "Open to other pharmacies", but neither `faTruck`/`faShippingFast` nor
// `faRefresh` is in nuxt.config.js's FontAwesome whitelist (checked against
// REGULAR_ICONS/SOLID_ICONS there before picking icon names) — unregistered
// icons render blank. `faSync` IS registered, so "Open to other pharmacies"
// keeps a refresh-style icon ('sync'); "Mail-order pharmacy" falls back to
// the registered 'envelope' (`faEnvelope`), which still reads as "mail".
//
// task-d2 re-skin (matching the MedicareCheckup platform's StepPharmacy.vue):
// `:resultsCount` now wires live results into GuidanceCard's connected
// dropdown (same panel treatment DoctorsStep/HospitalsStep/MedicationsStep
// already use) instead of the old plain results box, and `:minQueryLength='1'`
// mirrors the platform's `q.trim().length >= 1` — pharmacy chain names are
// short ("CVS") and the search is already ZIP-scoped, unlike Doctors/
// Hospitals/Medications which keep GuidanceCard's 2-char default. There's no
// manual-add here (no `manualLabel` passed) — same as the platform, which has
// no manual pharmacy form either.
//
// The platform's SavedPharmacy also carries `distance` into the stored list,
// but flow's Pharmacy serializer (flow/consults/serializers.py:47-59) does
// NOT declare that field — it's intentionally excluded from the 12-field
// whitelist in add() below. To still show it on a saved row (platform
// parity — right-aligned "0.4 mi", matching live search rows), we keep a
// SESSION-ONLY `distanceByNpi` map (same pattern as DoctorsStep's
// relationshipByNpi pill): populated when add() is called with a search
// result's distance, read by pharmDistance() below, never sent to the
// server. A pharmacy loaded from an already-saved survey has no entry in
// the map (this session never searched for it) and simply shows no
// distance — same documented limitation DoctorsStep's pillFor() already
// accepts for the relationship badge.
import GuidanceCard from '../parts/GuidanceCard.vue'
import SelectedItem from '../parts/SelectedItem.vue'
import SearchScopeRow from '../parts/SearchScopeRow.vue'
import searchScope from '~/mixins/checkup-search-scope'

export default {
    components: { GuidanceCard, SelectedItem, SearchScopeRow },
    mixins: [searchScope],
    // Radius this step's search hard-coded before the client could change it
    // (see mixins/checkup-search-scope.js) — pharmacy chains are dense, so it
    // starts tighter than the 25 miles doctors/hospitals use.
    searchDistance: 10,
    props: {
        target: { type: Object, required: true },
        // Copy is a prop so /renewal/* can reframe this step for a
        // returning client ("Are these still your…") while /checkup/*
        // keeps its first-visit wording (#656). The defaults are the
        // checkup strings verbatim, so every existing caller renders
        // byte-identical output without passing anything.
        heading: { type: String, default: 'Where do you get your prescriptions?' },
        lede: { type: String, default: 'A plan\'s "preferred" pharmacy can mean noticeably lower copays on the exact same drugs.' },
    },
    data () {
        return {
            results: [],
            searching: false,
            // Monotonic guard against out-of-order search responses — same
            // rationale as DoctorsStep.searchSeq.
            searchSeq: 0,
            // See the SESSION-ONLY distanceByNpi note above the imports.
            distanceByNpi: {},
        }
    },
    methods: {
        async onSearch (query) {
            const seq = ++this.searchSeq
            this.query = query
            if (!query) { this.results = []; this.searching = false; return }
            this.searching = true
            // Mirrors form/Pharmacies.vue:145-160's searchForm exactly
            // (query, zip_code, distance, page, page_size) — zip and distance
            // are now the client's own picks (SearchScopeRow, above).
            const resp = await this.$api.searchPharmacies(
                { query, zip_code: this.zip, distance: this.distance, page: 0, page_size: 10 },
                { spinner: false },
            ).catch(() => null)
            if (seq !== this.searchSeq) { return } // stale — a newer search owns the state
            this.searching = false
            // Oracle's pharmacy_search returns a flat result (mirrors
            // form/Pharmacies.vue's `results`, confirmed against the Pharmacy
            // serializer, flow/consults/serializers.py:47-59) — no
            // plural/singular location split like provider_search.
            this.results = resp?.results || []
        },
        isAdded (r) { return (this.target.pharmacies || []).some(p => p.npi === r.npi) },
        add (r) {
            // Whitelisted to match the Pharmacy serializer exactly (flow/
            // consults/serializers.py:47-59) — raw search results carry an
            // extra `distance` field (miles from the search zip) that the
            // serializer doesn't declare and must never be persisted onto
            // the survey. Remembered session-only instead (see the
            // distanceByNpi note above) so the saved row can still show it.
            if (r.npi != null && r.distance != null) { this.$set(this.distanceByNpi, String(r.npi), r.distance) }
            // Reassign rather than push() — target.pharmacies is proxied
            // through to the page's reactive this.form.pharmacies, and Vue's
            // deep watcher in mixins/survey-form.js hands the SAME array
            // reference as both newValue/oldValue on an in-place mutation, so
            // its `_.isEqual(newValue, oldValue)` guard would silently
            // swallow the change and nothing would autosave.
            this.target.pharmacies = [
                ...this.target.pharmacies,
                {
                    name: r.name,
                    street: r.street,
                    unit: r.unit,
                    city: r.city,
                    state: r.state,
                    zip_code: r.zip_code,
                    npi: r.npi,
                    phone: r.phone,
                    in_network: r.in_network,
                    preferred: r.preferred,
                    latitude: r.latitude,
                    longitude: r.longitude,
                },
            ]
            this.setOptOut(false)
            this.results = []
            this.$refs.card.clearSearch()
        },
        remove (i) { this.target.pharmacies = this.target.pharmacies.filter((_, idx) => idx !== i) },
        setOptOut (v) { this.$set(this.target, 'no_pharmacies', v) },
        onClearAndOptOut () {
            this.target.pharmacies = []
            this.setOptOut(true)
        },
        setMailOrder (v) { this.$set(this.target, 'prefer_mail_order', v) },
        setOtherOk (v) { this.$set(this.target, 'other_pharmacies_ok', v) },
        addrFor (p) {
            return [p.street, p.unit, `${p.city}, ${p.state} ${p.zip_code}`].filter(Boolean).join(', ')
        },
        // Right-aligned distance badge shared by search-result rows (raw
        // `r.distance`) and saved rows (the session-only distanceByNpi
        // memory) — rounded to 1 decimal, matching the same browser-QA fix
        // already applied to HospitalsStep.resultMeta(): raw Oracle
        // distances aren't guaranteed to be clean single-decimal values.
        milesLabel (d) { return `${Number(d).toFixed(1)} mi` },
        pharmDistance (p) {
            const d = p.npi != null ? this.distanceByNpi[String(p.npi)] : null
            return d != null ? this.milesLabel(d) : null
        },
    },
}
